<template>
  <div class="page background-main">
    <!-- <AppBackgroundCanvas /> -->
    <div class="page_stage_shell">
      <div class="page_stage">
        <div class="app_body">
          <div class="main_container">
            <RouterView v-if="isCommunityRoute" class="route_overlay" />
            <template v-if="isDeviceSelected">
              <div v-show="!isCommunityRoute" class="root_mount">
                <Root />
              </div>
            </template>
            <template v-else>
              <template v-if="!isCommunityRoute">
                <empty v-if="isShowEmpty" />
                <selector v-else />
              </template>
            </template>
            <cdialog />
            <AuthRecoveryDialog />
            <SwitchKeySyncDialog />
            <loading />
            <feedback />
            <user_center />
            <setting />
            <keyboard_appearance_floating />
            <software_theme_floating />

            <app_update_notice />
            <service_update_notice />
            <Pair24g />
            <Firmware_upgrade />
            <dev_brand_switcher />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, watch } from 'vue'
import { RouterView, useRoute } from 'vue-router'
import cdialog from '@/components/cdialog/cdialog.vue'
import loading from '@/components/loading/loading.vue'
import feedback from './components/feedback/feedback.vue'
import empty from './components/empty/empty.vue'
import selector from './components/selector/selector.vue'
// setBoxBool 是 setting.vue 的具名导出（App 的 watch 依赖它），故 setting 保持静态导入。
// Pair24g / Firmware_upgrade 被 setting 链静态引用而必然进入首包，异步化无收益，一并保持静态。
import setting, { setBoxBool } from './components/setting/setting.vue'
import Pair24g from './components/pair_24g/pair_24g.vue'
import Firmware_upgrade from './components/firmware_upgrade/firmware_upgrade.vue'
import AsyncViewLoading from '@/components/common/AsyncViewLoading.vue'
import { DeviceManager, DeviceProtocol } from './service/device'
import { DevicePid, EEvisionDeviceList } from './define/define'
import { isElectron } from '@libs/utils/envUtils'
import { closeKeyboardAppearancePanel } from '@/service/keyboard_appearance'
import { closeAllAppearancePanels } from '@/service/appearance_panel_registry'

// 首屏用不到的重子树与浮层按需分包，缩小首包、加快首屏渲染。
// Root 位于 v-if="isDeviceSelected" 内，未选设备时不会下载其庞大的配置界面子树。
//
// 选设备后 currentPid 一置上就会立即移除选择器、挂载 Root。为避免此刻 chunk 尚未就绪
// 而出现空白，采用两层机制：
//   1) loadingComponent：机制上的正确性保证——chunk 未就绪时渲染占位而非空白。
//   2) prefetchRoot（见下方 watch）：性能优化——设备列表出现后提前预热 chunk。
// delay 设为 200ms，使已被预热（常见路径）的加载在占位出现前即完成，做到既不空白也不闪 spinner；
// 仅当加载确实较慢时才显示占位。
const loadRoot = () => import('./components/main/root.vue')
const Root = defineAsyncComponent({
  loader: loadRoot,
  loadingComponent: AsyncViewLoading,
  delay: 200,
})
const user_center = defineAsyncComponent(() => import('./components/user_center/user_center.vue'))
const AuthRecoveryDialog = defineAsyncComponent(() => import('@/components/auth/AuthRecoveryDialog.vue'))
const SwitchKeySyncDialog = defineAsyncComponent(() => import('@/components/cdialog/SwitchKeySyncDialog.vue'))
const keyboard_appearance_floating = defineAsyncComponent(() => import('@/components/setting/keyboard_appearance_floating.vue'))
const software_theme_floating = defineAsyncComponent(() => import('@/components/setting/software_theme_floating.vue'))
const service_update_notice = defineAsyncComponent(() => import('@/components/service_update/service_update_notice.vue'))
const app_update_notice = defineAsyncComponent(() => import('./components/app_update/app_update_notice.vue'))
const dev_brand_switcher = defineAsyncComponent(() => import('@/components/dev/dev_brand_switcher.vue'))

import { trackWebActivity } from '@/utils/appEffect'

const isDeviceSelected = computed(() => DeviceManager.instance.state.currentPid != DevicePid.None)
const route = useRoute()
const isCommunityRoute = computed(() => route.meta.layout === 'config-share')
const isShowEmpty = computed(() => {
  return isElectron() && DeviceManager.instance.state.webDeviceList.concat(DeviceManager.instance.state.deviceList).length == 0
})

const allDeviceList = computed(() => DeviceManager.instance.state.webDeviceList.concat(DeviceManager.instance.state.deviceList))

// Root chunk 预热：设备列表出现后立即在后台下载 Root（fire-and-forget），
// 使用户随后点击设备时 chunk 已就绪，进入操作页无需等待。仅为性能优化，
// 加载失败无需处理——正式挂载时异步组件会自行重试并有 loadingComponent 兜底。
// 与自动跳转逻辑关注点不同，故独立成单独的 watch。
let isRootPrefetched = false
watch(
  () => allDeviceList.value.length > 0,
  (hasDevice) => {
    if (!hasDevice || isRootPrefetched) return
    isRootPrefetched = true
    loadRoot().catch(() => {
      isRootPrefetched = false
    })
  },
  { immediate: true },
)

watch(allDeviceList, (list, oldList) => {
  if (DeviceManager.instance.state.currentPid !== DevicePid.None || DeviceManager.instance.state.isSelectingDevice) return

  // 同一台物理设备在 Electron 下会同时出现在 webDeviceList(HID) 与 deviceList(EXE) 两个列表里，
  // 且 EXE 回包比 HID 慢。若直接用 concat 长度判断，会把一个设备算成两个，导致"只有一个设备"
  // 判定失败（length 变成 2，永不自动跳转）。因此按设备 ID 去重后再判断唯一设备数量。
  const uniqueIds = new Set(list.map((device) => device.ID))
  const oldUniqueCount = new Set(oldList.map((device) => device.ID)).size

  // 连接多台不同设备时不自动跳转，交给用户在选择器页面手动选择。
  // 记忆跳转与唯一设备跳转都必须建立在"当前只有一台设备"之上，
  // 否则记忆逻辑会在多设备场景下把用户直接带进上次用过的设备操作页。
  if (uniqueIds.size !== 1) return

  const remembered = DeviceManager.instance.getRememberedDeviceSelection()
  if (remembered) {
    const hasWebDevice = DeviceManager.instance.state.webDeviceList.some((device) => device.ID === remembered.pid)
    const hasExeDevice = DeviceManager.instance.state.deviceList.some((device) => device.ID === remembered.pid)
    if (hasWebDevice || hasExeDevice) {
      const mode = hasWebDevice ? DeviceProtocol.hid_web : remembered.mode === DeviceProtocol.dll_exe && hasExeDevice ? DeviceProtocol.dll_exe : DeviceProtocol.hid_web
      DeviceManager.instance.selectCurrentDevice(remembered.pid, mode)
      return
    }
  }

  // 只在从 0 个（去重后）设备变为 1 个设备时自动跳转（初次发现 / 重连）。
  // 若 oldList 已有设备（如用户点击"继续授权"后列表刷新），不触发自动跳转。
  if (oldUniqueCount === 0 && DeviceManager.instance.state.currentPid === DevicePid.None) {
    const deviceId = [...uniqueIds][0]
    // 优先取 HID 列表里的设备对象（EXE 更慢，HID 先到即先跳转）。
    const device = list.find((item) => item.ID === deviceId)
    if (!device) return
    // EEvision 设备需要弹窗引导打开外部程序，不自动跳转
    if (EEvisionDeviceList.get(device.ID) != undefined) return
    // FE87V2 旧 24G 固件需要弹窗引导升级，不自动跳转（保留在选择器页面让用户点击触发弹窗）
    if (device.ID == DevicePid.IrokFe87V2_24g && device.ProductName && device.ProductName.indexOf('Fe87 V2') > -1) return
    // 唯一设备若在 HID 列表中则用 hid_web，否则（仅 EXE 可见）用 dll_exe。
    const mode = DeviceManager.instance.state.webDeviceList.some((item) => item.ID === deviceId) ? DeviceProtocol.hid_web : DeviceProtocol.dll_exe
    DeviceManager.instance.selectCurrentDevice(device.ID, mode)
  }
})

watch(setBoxBool, (value) => {
  if (value) {
    closeAllAppearancePanels()
  }
})

watch(isDeviceSelected, (value) => {
  if (!value) {
    closeKeyboardAppearancePanel()
  }
})

// 网页驱动增加日活, 要求当前使用的语言是中文, 进行统计
trackWebActivity()

if (process.env.NODE_ENV === 'development' && typeof electronAPI !== 'undefined') {
  electronAPI.openDev()
}
</script>

<style scoped lang="less">
.page {
  width: 100%;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  position: relative;
  font-family: var(--font-main);
}

.page_stage_shell {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: stretch;
  align-items: stretch;
  flex-shrink: 0;
  overflow: hidden;
  z-index: 1;
}

.page_stage {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  // padding: 8px;
  backface-visibility: hidden;
  // border-radius: 20px;
  // background: var(--shell-surface);
  // border: 1px solid rgba(var(--color-brand-1-rgb), 0.06);
  overflow: hidden;
}

.app_body {
  flex: 1;
  min-height: 0;
  display: flex;
  border-radius: 0;
  overflow: hidden;
}

.main_container {
  flex: 1;
  display: flex;
  position: relative;
  min-height: 0;
}

.global_appearance_entry {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 180;
  min-width: 0;
  background: var(--glass-ghost-bg);
  backdrop-filter: var(--glass-ghost-blur);
  border: 1px solid var(--glass-ghost-border);
  color: var(--shell-text-primary);

  &:hover {
    background: var(--glass-ghost-hover-bg);
    border-color: var(--glass-ghost-hover-border);
  }
}

.route_overlay {
  position: absolute;
  inset: 0;
  z-index: 120;
  background: transparent;
}

.root_mount {
  display: contents;
}
</style>
